import styled, { keyframes } from "styled-components";
import { SUBSCRIPTION_THEMES, SUBSCRIPTION_COLORS } from "../SubscriptionComponents.constants";

interface ISubscriptionPlanCardSkeletonProps {
  className?: string;
  variant?: "basic" | "premium";
}

const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonShimmer = styled.div<{ $variant: "basic" | "premium" }>`
  animation: ${shimmerAnimation} 2.5s infinite ease-in-out;
  background: ${({ $variant }) =>
    $variant === "basic"
      ? "linear-gradient(to right, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.05) 100%)"
      : "linear-gradient(to right, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.05) 100%)"};
  background-size: 1000px 100%;
  background-color: ${({ $variant }) =>
    $variant === "basic" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)"};
  border-radius: 8px;
`;

const SkeletonBox = styled.div`
  background-color: ${SUBSCRIPTION_COLORS.skeletonBg};
  border-radius: 8px;
`;

export default function SubscriptionPlanCardSkeleton({
  className = "",
  variant = "premium",
}: ISubscriptionPlanCardSkeletonProps) {
  const theme =
    variant === "basic"
      ? SUBSCRIPTION_THEMES.basic
      : SUBSCRIPTION_THEMES.premium;

  return (
    <div
      className={`relative flex flex-col max-w-[324px] min-w-[320px] rounded-xl shadow-lg overflow-hidden border border-gray-200 ${className}`}
    >

      {/* Header Section Skeleton */}
      <div className="px-5 pt-5" style={{ backgroundColor: theme.bg }}>
        {/* Title */}
        <SkeletonShimmer $variant={variant} className="h-8 w-48 mb-6" />

        {/* Description text */}
        <SkeletonShimmer $variant={variant} className="h-4 w-full mb-2" />
        <SkeletonShimmer $variant={variant} className="h-4 w-4/5 mb-8" />

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2">
          <SkeletonShimmer $variant={variant} className="h-12 w-32" />
          <SkeletonShimmer $variant={variant} className="h-5 w-16" />
        </div>

        {/* Billing label */}
        <SkeletonShimmer $variant={variant} className="h-4 w-32 mb-6" />
      </div>

      {/* Action Buttons Section Skeleton */}
      <div className="px-5 pb-6 pt-0" style={{ backgroundColor: theme.bg }}>
        {/* Primary button */}
        <SkeletonShimmer
          $variant={variant}
          className="h-10 w-full mb-3 rounded-md"
        />

        {/* Secondary button */}
        <SkeletonShimmer $variant={variant} className="h-10 w-full rounded-md" />
      </div>

      {/* Features Section Skeleton */}
      <div
        className="px-5 pb-8 pt-6 flex-grow"
        style={{ backgroundColor: SUBSCRIPTION_COLORS.white }}
      >
        {/* Features title */}
        <SkeletonBox className="h-6 w-48 mb-6 justify-self-center" />

        {/* Feature items */}
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="flex items-start gap-3 mb-4">
            {/* Checkmark circle */}
            <SkeletonBox className="h-5 w-5 rounded-full flex-shrink-0 mt-0.5" />
            {/* Feature text */}
            <div className="flex-1">
              <SkeletonBox className="h-4 w-full mb-1" />
              {item === 2 && <SkeletonBox className="h-4 w-3/4" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
